import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

export type DimensionItem = {
  aspectRatio: string;
  isPublished: boolean;
  /** When true, the AR video doesn't exist yet and will be created on click */
  isNew?: boolean;
  /** When true, this dimension is currently being created */
  isCreating?: boolean;
};

type Props = {
  dimensions: DimensionItem[];
  selectedDimension: string;
  onSelectDimension: (dimension: string) => void;
  theme?: 'green' | 'yellow';
  publishingDimension?: string | null;
  publishStartTime?: Date | null;
  publishedBy?: string;
};

const DimensionsSelector = ({
  dimensions,
  selectedDimension,
  onSelectDimension,
  theme = 'green',
  publishingDimension,
  publishStartTime,
  publishedBy,
}: Props) => {
  return (
    <Container data-testid="dimensions-selector">
      {dimensions.map(dimension => {
        const isPublishing = dimension.aspectRatio === publishingDimension;
        const showTooltip = !dimension.isPublished || isPublishing;
        const isDisabled = dimension.isCreating === true;
        const tooltipContent = isPublishing
          ? [
              'Publishing...',
              publishStartTime &&
                `Started ${publishStartTime.toLocaleTimeString()}`,
              publishedBy && `By ${publishedBy}`,
            ]
              .filter(Boolean)
              .join(' · ')
          : 'Draft';

        return (
          <DimensionButton
            theme={theme}
            isPublishing={isPublishing}
            isPublished={dimension.isPublished}
            isDraft={!dimension.isPublished && !isPublishing}
            disabled={isDisabled}
            key={dimension.aspectRatio}
            isSelected={dimension.aspectRatio === selectedDimension}
            isNew={dimension.isNew || false}
            onClick={() => onSelectDimension(dimension.aspectRatio)}
            data-tooltip-id={
              showTooltip
                ? `dimension-${dimension.aspectRatio}-tooltip`
                : undefined
            }
            data-tooltip-content={showTooltip ? tooltipContent : undefined}
          >
            {dimension.isNew && '+ '}
            {dimension.aspectRatio}
            {showTooltip && (
              <Tooltip
                id={`dimension-${dimension.aspectRatio}-tooltip`}
                style={{
                  padding: '8px',
                  fontSize: '10px',
                  lineHeight: '120%',
                  zIndex: '10',
                }}
              />
            )}
          </DimensionButton>
        );
      })}
    </Container>
  );
};

export default DimensionsSelector;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DimensionButton = styled.button<{
  isSelected: boolean;
  isNew: boolean;
  theme: 'green' | 'yellow';
  isPublished: boolean;
  isPublishing?: boolean;
  isDraft?: boolean;
}>`
  background-color: transparent;
  text-wrap: nowrap;
  color: ${props => {
    if (props.isPublishing) return 'var(--background)';
    if (props.isDraft)
      return props.isSelected ? 'var(--background)' : 'var(--text-muted)';
    let defaultColor = 'var(--primary)';
    let selectedColor = 'var(--background)';
    if (props.theme === 'yellow') {
      defaultColor = 'var(--border)';
      selectedColor = 'var(--accent)';
    }
    if (props.isNew) {
      defaultColor = 'var(--text-muted)';
    }
    if (!props.isPublished) {
      defaultColor = 'var(--border)';
    }
    return props.isSelected ? selectedColor : defaultColor;
  }};
  border: 1px ${props => (props.isNew ? 'dashed' : 'solid')}
    ${props => {
      if (props.isPublishing) return 'var(--accent)';
      if (props.isDraft) return 'var(--text-muted)';
      let defaultColor = 'var(--primary)';
      let selectedColor = 'var(--primary)';
      if (props.theme === 'yellow') {
        defaultColor = 'var(--border)';
        selectedColor = 'var(--border)';
      }
      if (props.isNew) {
        defaultColor = 'var(--text-muted)';
      }
      if (!props.isPublished) {
        defaultColor = 'var(--border)';
      }
      return props.isSelected ? selectedColor : defaultColor;
    }};
  background-color: ${props => {
    if (props.isPublishing) return 'var(--accent)';
    if (props.isDraft)
      return props.isSelected ? 'var(--text-muted)' : 'transparent';
    let defaultColor = 'transparent';
    let selectedColor = 'var(--primary)';
    if (!props.isPublished) {
      defaultColor = 'transparent';
    }
    if (props.theme === 'yellow') {
      selectedColor = 'var(--border)';
    }
    return props.isSelected ? selectedColor : defaultColor;
  }};
  border-radius: 4px;
  height: 28px;
  padding: 0 12px;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:disabled {
    cursor: not-allowed;
    color: var(--border);
    border-color: var(--border);
    background-color: transparent;
  }
  &:hover:not(:disabled) {
    opacity: 0.8;
  }
`;
